import React, { useEffect, useState } from "react";
import "./payment.css";
import TypingAnimation from "./TypingAnimation";
import { useDispatch, useSelector } from "react-redux";
import {
  actions,
  getPaymentDetails,
  getPreloader,
} from "../redux/main-reducer";
import axios from "axios";
import buttonLoader from "../../images/button-loader.svg";
import { attachImageUrl } from "../../utils/attachImageUrl";

const OTP_LENGTH = 6;

function Payment({ isShowForm, onShowForm, setCommonErrorMessage }) {
  const preloader = useSelector(getPreloader);
  const paymentDetails = useSelector(getPaymentDetails);
  const searchParams = new URLSearchParams(window.location.search);
  const isInvType = searchParams.get("inv_type") === "4";

  const dispatch = useDispatch();

  const API_ROOT = process.env.REACT_APP_API_ROOT;

  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [smsVerificationState, setSmsVerificationState] = useState(false);
  const [smsVerification, setSmsVerification] = useState("");
  const [resendTimer, setResendTimer] = useState(59);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageExpireDate, setErrorMessageExpireDate] = useState("");
  const [errorMessageSMS, setErrorMessageSMS] = useState("");
  const [payID, setPayID] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSmsRequired, setIsSmsRequired] = useState(false);

  const [showExpiryFields, setShowExpiryFields] = useState(false);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const monthRef = React.useRef(null);
  const yearRef = React.useRef(null);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const inputRefs = React.useRef([]);

  const handleMonthChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // только цифры
    if (value.length > 2) value = value.slice(0, 2);
    setMonth(value);

    // автофокус на поле года, если месяц введён
    if (value.length === 2) {
      const monthNum = parseInt(value, 10);
      if (monthNum >= 1 && monthNum <= 12) {
        yearRef.current?.focus();
      }
    }
  };

  const handleYearChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 2) value = value.slice(0, 2);
    setYear(value);
  };

  const isFormValid =
    cardNumber.length === 16 && /^\d{2}\/\d{2}$/.test(expiryDate);
  const isSmsValid = /^\d{6}$/.test(smsVerification);

  useEffect(() => {
    let timer;
    if (isTimerActive && resendTimer > 0) {
      timer = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsTimerActive(false);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, isTimerActive]);

  useEffect(() => {
    if (showExpiryFields && monthRef.current) {
      monthRef.current.focus();
    }
  }, [showExpiryFields]);

  const handleCardNumberChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    setCardNumber(rawValue.slice(0, 16));
    if (rawValue?.length === 16 && !showExpiryFields) {
      setShowExpiryFields(true);
      return;
    }
    if (rawValue?.length === 16 && showExpiryFields) {
      monthRef.current?.focus();
    }
  };

  // const handleExpiryDateChange = (e) => {
  //   setErrorMessageExpireDate("");
  //   const value = e.target.value.replace(/\D/g, "");
  //   if (value.length > 2) {
  //     setExpiryDate(value.slice(0, 2) + "/" + value.slice(2, 4));
  //   } else {
  //     setExpiryDate(value);
  //   }
  // };

  // const formatCardNumber = () => {
  //   const totalDigits = 16;
  //   const maskedArray = "*".repeat(totalDigits).split("");

  //   for (let i = 12; i < cardNumber.length; i++) {
  //     maskedArray[i] = cardNumber[i];
  //   }

  //   return (
  //     maskedArray
  //       .join("")
  //       .match(/.{1,4}/g)
  //       ?.join(" ") || ""
  //   );
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setCommonErrorMessage("");
    setErrorMessageExpireDate("");
    setIsSubmitting(true);

    try {
      const key = new URLSearchParams(window.location.search).get("key");
      // const formattedExpiryDate = expiryDate.replace("/", "");
      const parsedMonth = month ? parseInt(month, 10) : "";
      const parsedYear = year ? parseInt(year, 10) : "";

      const currentYearFull = new Date().getFullYear(); // например, 2025
      const currentYear = currentYearFull % 100; // последние две цифры: 25

      if (!cardNumber || cardNumber?.length < 16) {
        setErrorMessage("Введите номер карты");
        return;
      }

      if (!month || !year) {
        setErrorMessageExpireDate("Введите срок карты");
        return;
      }

      if (isNaN(parsedMonth) || parsedMonth < 1 || parsedMonth > 12) {
        setErrorMessageExpireDate("Неверный месяц");
        return;
      }

      if (
        isNaN(parsedYear) ||
        parsedYear < currentYear || // нельзя платить в прошлом
        parsedYear > currentYear + 20 // ограничим запас 20 лет вперёд
      ) {
        setErrorMessageExpireDate("Неверный год");
        return;
      }

      const formattedExpiryDate = `${month}${year}`; // например, "0426"

      const data = {
        card_expiry: formattedExpiryDate,
        card_number: cardNumber,
        key: key,
        is_by_phone: false,
      };

      const response = await axios.post(
        `${API_ROOT}/api/v1/ecomm/payments/pay/create`,
        data
      );

      if (response.data && response.data.status === "Success") {
        const isSmsRequired = response?.data?.data?.is_sms_required;
        const payId = response?.data?.data?.pay_id;
        setIsSmsRequired(isSmsRequired);
        setPayID(payId);

        // Если SMS не требуется, сразу вызываем верификацию без OTP
        if (!isSmsRequired) {
          await handleVerification(null, false, payId); // Передаем payId напрямую
        } else {
          // Если SMS требуется, показываем форму верификации
          setSmsVerificationState(true);
          onShowForm(true);
          setResendTimer(59);
          setIsTimerActive(true);
        }
      } else {
        throw new Error(
          response.data.error_note || "Не удалось инициировать оплату"
        );
      }
    } catch (error) {
      console.error("Error during payment request:", error);
      if (error.response?.data?.error_code === 1001) {
        setErrorMessage("На вашей карте недостаточно средств");
      } else if (error.response?.data?.error_code === 1002) {
        setErrorMessage("Карта не найдена");
      } else if (error.response?.data?.error_code === 1003) {
        setErrorMessage("Недостаточно прав для осуществление этой операции");
      } else if (error.response?.data?.error_code === 1032) {
        setErrorMessage("Карта уже существует в нашей системе");
      } else if (error.response?.data?.error_code === 1033) {
        setErrorMessage(
          "Карта уже верифицирована  или срок действия кода подтверждения истек."
        );
      } else if (error.response?.data?.error_code === 1034) {
        setErrorMessage("Карта не активна");
      } else if (error.response?.data?.error_code === 1044) {
        setErrorMessage("Карта не привязана к мерчанту");
      } else if (error.response?.data?.error_code === 1059) {
        setErrorMessage(
          "Пожалуйста, проверьте и попробуйте снова, так как сумма, которую вы хотите оплатить, превышает указанную сумму."
        );
      } else if (error.response?.data?.error_code === 1020) {
        setErrorMessageExpireDate("Недостаточно средств на балансе мерчанта");
      } else if (error.response?.data?.error_code === 1021) {
        setErrorMessageExpireDate("Срок действия карты истек");
      } else if (error.response?.data?.error_code === 1024) {
        setErrorMessageExpireDate("Срок действия карты указан неверно");
      } else if (isInvType && error.response?.data?.error_code === 1056) {
        window.location.replace(`${paymentDetails?.failure_url}`);
      } else {
        setCommonErrorMessage(error?.response?.data?.error_note || "Произошла ошибка. Пожалуйста, попробуйте позже");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerification = async (otpCode, isSmsRequiredParam = isSmsRequired, payIdParam = payID) => {
    console.log("Отправка OTP:", otpCode, "isSmsRequired:", isSmsRequiredParam, "payIdParam:", payIdParam);

    // Валидация OTP только если он требуется
    if (isSmsRequiredParam && (!otpCode || !/^[a-zA-Z0-9]{6}$/.test(otpCode))) {
      console.warn("OTP невалиден");
      return;
    }

    // e.preventDefault();
    setErrorMessage("");
    setErrorMessageExpireDate("");
    setCommonErrorMessage("");

    try {
      dispatch(actions.setPreloader(true));

      console.log("payID:", payIdParam);
      console.log("isSmsRequiredParam:", isSmsRequiredParam);

      // Формируем data в зависимости от isSmsRequired
      const data = isSmsRequiredParam 
        ? {
            confirm_code: otpCode,
            pay_id: payID,
          }
        : {
            pay_id: payIdParam,
          };

      const response = await axios.post(
        `${API_ROOT}/api/v1/ecomm/payments/pay/confirm`,
        data
      );

      console.log("SMS Verification Success:", response);

      if (response.data && response.data.status === "Success") {
        dispatch(actions.setPaymentDetails(attachImageUrl(response?.data?.data)));
        console.log(response?.data?.data);
        // window.location.replace(`${paymentDetails?.success_url}`);
        setTimeout(() => {
          dispatch(actions.setPaymentState(true));
          dispatch(actions.setPreloader(false));
          dispatch(actions.setPaymentDetails(attachImageUrl(response?.data?.data)));
        }, 1000);
      } else {
        throw new Error(
          response.data.error_note || "Не удалось подтвердить оплату"
        );
      }
    } catch (error) {
      console.error("Error during SMS verification:", error);
      if (error.response?.data?.error_code === 1008) {
        setErrorMessageSMS("Введенный SMS-код не верный");
      } else if (error.response?.data?.error_code === 1027) {
        setErrorMessageSMS(
          "Карта заблокирована. Попробуйте снова через 5 минут"
        );
      } else if (error.response?.data?.error_code === 1028) {
        setErrorMessageSMS(
          "Карта заблокирована. Попробуйте снова через 5 минут"
        );
      } else if (error.response?.data?.error_code === 1029) {
        setErrorMessageSMS(
          "Повторно запросить SMS-код можно только через 1 минуту."
        );
      } else if (error.response?.data?.error_code === 1030) {
        setErrorMessageSMS(
          "Срок действия кода подтверждения платежа истёк. Код действителен в течение 1 минуты"
        );
      } else if (error.response?.data?.error_code === 1031) {
        setErrorMessageSMS("Время повторной отправки SMS истекло.");
      } else if (error.response?.data?.error_code === 1004) {
        setErrorMessageSMS(
          "Функция отправки SMS-кода не работает. Пожалуйста попробуйте позже"
        );
      } else if (error.response?.data?.error_code === 1058) {
        setErrorMessageSMS(
          "SMS уже было отправлено. Пожалуйста, попробуйте снова через 1 минуту."
        );
      } else if (isInvType) {
        console.log("isInvType", isInvType);
        console.log("isInvType", error);
        window.location.replace(`${paymentDetails?.failure_url}`);
        setErrorMessageSMS("Ошибка проверки SMS-кода");
      } else {
        setErrorMessageSMS("Ошибка проверки SMS-кода");
      }
      dispatch(actions.setPreloader(false));
    }
  };

  const handleResend = async () => {
    setErrorMessage("");
    setCommonErrorMessage("");
    setErrorMessageExpireDate("");
    setResendTimer(59);
    setIsTimerActive(true);

    try {
      const data = {
        pay_id: payID,
      };

      const response = await axios.post(
        `${API_ROOT}/api/v1/ecomm/payments/pay/sms/resend`,
        data
      );

      console.log("SMS Resend Response:", response);

      if (response.data && response.data.status === "Success") {
        console.log("SMS code resent successfully");
      } else {
        throw new Error(
          response.data.error_note || "Не удалось повторно отправить код"
        );
      }
    } catch (error) {
      console.error("Error during SMS resend:", error);
      if (error.response?.data?.error_code === 1031) {
        setErrorMessage("Время ожидания отправки SMS-кода истекло");
      } else {
        setErrorMessage(error.message || "Ошибка повторной отправки СМС-кода");
      }
      setIsTimerActive(false);
    }
  };

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value[0]; // Вставляем только первую цифру
    setOtp(newOtp);

    // Перейти к следующему полю
    if (index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Отправка только когда все 6 полей заполнены
    const otpComplete = newOtp.every((val) => val.length === 1);
    if (otpComplete) {
      const otpCode = newOtp.join("");
      handleVerification(otpCode);
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData
      .getData("Text")
      .replace(/[^a-zA-Z0-9]/g, "");

    if (pasteData.length === OTP_LENGTH) {
      const newOtp = pasteData.split("").slice(0, OTP_LENGTH);
      setOtp(newOtp);

      // автофокус на последнем
      inputRefs.current[OTP_LENGTH - 1]?.focus();

      // автоотправка
      handleVerification(newOtp.join(""));
    }

    e.preventDefault(); // предотвратить вставку в одно поле
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  console.log("PaymentDetails", paymentDetails);

  return (
    <div className="payment-container">
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div className={`payment-container-info ${preloader ? "blur" : ""}`}>
      {!isShowForm && (
            <form
              className="payment-form"
              onSubmit={handleSubmit}
              autoComplete="on"
            >
              <div className="payment-fields-block">
                <div className="form-group">
                  <input
                    id="ccnumber"
                    name="cardNumber"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    type="tel"
                    pattern="[0-9\s]{13,19}"  
                    value={cardNumber.match(/.{1,4}/g)?.join(" ") || ""}
                    onChange={handleCardNumberChange}
                    placeholder="Номер карты"
                    maxLength="19"
                    disabled={isSubmitting}
                    // Атрибуты для iOS Safari
                    autocorrect="off"
                    spellcheck="false"
                    className={`input-field-card ${
                      isSubmitting ? "input-field-card_disabled" : ""
                    } ${errorMessage ? "input-field-card_error" : ""}`}
                  />
                  {errorMessage && (
                    <div className="error-message">{errorMessage}</div>
                  )}
                </div>
                {showExpiryFields && (
                  <div className="form-group expiry-group">
                    <div className="expiry-fields">
                      <input
                        id="cc-exp-month"
                        name="expiryMonth"
                        inputMode="numeric"
                        autoComplete="cc-exp-month"
                        type="tel"
                        value={month}
                        onChange={handleMonthChange}
                        placeholder="MM"
                        maxLength={2}
                        disabled={isSubmitting}
                        ref={monthRef}
                        className={`input-field ${
                          isSubmitting ? "input-field_disabled" : ""
                        } ${errorMessageExpireDate ? "input-field_error" : ""}`}
                      />
                      <span className="expiry-separator">/</span>
                      <input
                        id="cc-exp-year"
                        name="expiryYear"
                        inputMode="numeric"
                        autoComplete="cc-exp-year"
                        type="tel"
                        value={year}
                        onChange={handleYearChange}
                        placeholder="ГГ"
                        maxLength={2}
                        disabled={isSubmitting}
                        ref={yearRef}
                        className={`input-field ${
                          isSubmitting ? "input-field_disabled" : ""
                        } ${errorMessageExpireDate ? "input-field_error" : ""}`}
                      />
                    </div>
                    {errorMessageExpireDate && (
                      <div className="error-message">
                        {errorMessageExpireDate}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* <div className="form-group">
              <input
                type="text"
                value={expiryDate}
                onChange={handleExpiryDateChange}
                placeholder=" "
                maxLength="5"
                disabled={smsVerificationState || isSubmitting}
                className={`${
                  smsVerificationState || isSubmitting
                    ? "input-field input-field_diabled"
                    : "input-field"
                }`}
              />
              <label>ММ/ГГ</label>
              {errorMessageExpireDate && (
                <div className="error-message">{errorMessageExpireDate}</div>
              )}
            </div> */}

              {/* {!smsVerificationState && ( */}
              <button
                className={`pay-button ${isSubmitting ? "_disable" : ""}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="button-loader">
                    <img
                      className="button-loader__image"
                      src={buttonLoader}
                      alt=""
                    />
                  </div>
                ) : (
                  "Оплатить"
                )}
              </button>
              {/* )} */}
            </form>
          )}
          {/* Захардкодил SMS на отрицание ! */}
          {smsVerificationState && isShowForm && (
            <>
              {/* <form className="payment-form" onSubmit={handleVerification}> */}
              {/* <div className="form-group">
                <input
                  type="text"
                  value={smsVerification}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setSmsVerification(value.slice(0, 6));
                  }}
                  placeholder=" "
                  maxLength="6"
                  className="input-field"
                />
                <label>СМС код</label>
                {errorMessageSMS && (
                  <div className="error-message">{errorMessageSMS}</div>
                )}
              </div> */}
              <div className="otp-wrapper">
                <div className="otp-wrapper__block">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className={`otp-input ${
                        errorMessageSMS && "otp-input_error"
                      }`}
                      value={digit}
                      onChange={(e) => handleChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      ref={(el) => {
                        if (el) inputRefs.current[index] = el;
                      }}
                    />
                  ))}
                </div>
                {errorMessageSMS && (
                  <div className="error-message">{errorMessageSMS}</div>
                )}
              </div>
              {isTimerActive ? (
                <div className="resend-sms">
                  Отправим код повторно через{" "}
                  {resendTimer.toString().padStart(2, "0")} секунд
                </div>
              ) : (
                <div className="resend-sms" onClick={handleResend}>
                  Отправить СМС повторно
                </div>
              )}
              <div
                className="resend-sms _m-t-4"
                onClick={() => onShowForm(false)}
              >
                Вернуться назад
              </div>
            </>
          )}
        </div>
        {isInvType && (
          <button
            type="button"
            className="download-button"
            onClick={() =>
              window.location.replace(`${paymentDetails?.failure_url}`)
            }
          >
            Закрыть
          </button>
        )}
      </div>
      {preloader && (
        <div className="loader">
          <div className="loading"></div>
          <div className="typing-container">
            <TypingAnimation
              texts={["Идёт оплата...", "Не закрывайте страницу..."]}
              typingInterval={70}
              pauseDuration={1000}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Payment;
